/* eslint-disable no-unused-vars */
import React, { useState, useEffect, Fragment } from 'react'
import { Box, Button, Divider, IconButton, Typography } from '@mui/material'
import '../../styles/devices.min.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faXmark,
  faAngleDown,
  faAngleUp
} from '@fortawesome/pro-regular-svg-icons'
import MobileNavigationBar from '../mobilePreview/MobileNavigationBar'
import { generalMobileStyles } from '../../styles/classes/MobilePreviewClasses'
import {
  getFirstKeyFromObject,
  formatSections,
  elementTypes
} from '../../utils/formBuilder'
import { SectionList } from 'react-native-web'
import EqServicesComponentV2 from '../mobilePreview/EqServicesComponentV2.js'
import { useTranslation } from 'react-i18next'
import { pendingTasksCounter } from '../../utils/mobilePreview'
import {
  calculateLineItemsSubTotal,
  calculateMaterialsSubTotal
} from '../../utils/global.js'
import ElementContainer from '../mobilePreview/ElementContainer.js'

const FormPreview = ({ onShowPreview, configurations, formName }) => {
  const { t } = useTranslation()
  const { classes } = generalMobileStyles()
  const [pageIndex, setPageindex] = useState(0) // we will manage pages in the future
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [dataObject, setDataObject] = useState({})

  const totalAmountDueValueRef = React.useRef(0)
  const dataObjectRef = React.useRef({})

  const extraData = React.useMemo(() => {
    return checkExtraData(configurations)
  }, [JSON.stringify(configurations)])

  const formattedSections = React.useMemo(() => {
    return formatSections(configurations[pageIndex]?.page_questions)
  }, [JSON.stringify(configurations)])

  const activeDD = React.useMemo(() => {
    return new Array(formattedSections.length).fill(true)
  }, [JSON.stringify(formattedSections)])

  const [activeDropDowns, setActiveDropDowns] = useState(activeDD)

  const pendingObj = React.useMemo(() => {
    const [unresolvedTasks, totalMandatoryQuestions] = pendingTasksCounter(
      configurations,
      dataObject
    )
    const percentage = (
      (1 - unresolvedTasks / totalMandatoryQuestions) *
      100
    ).toFixed(0)
    return {
      percentage,
      unresolvedTasks
    }
  }, [JSON.stringify(dataObject)])

  function checkExtraData (configurations) {
    let authFieldElement
    const lastItemWithTotalAmountDue = {
      sectionKey: undefined,
      questionKey: undefined
    }
    let authFieldSection
    const elementsWithTotalAmountDue = []
    // Loop through each page
    configurations.forEach(page => {
      // Loop through each page's questions
      page.page_questions.forEach(pageSection => {
        // Loop through each section
        Object.keys(pageSection).forEach(sectionKey => {
          const section = pageSection[sectionKey]
          // Loop through each question in the section
          section.questions.forEach(question => {
            const questionKey = getFirstKeyFromObject(question)
            const questionDetails = question[questionKey]
            if (questionDetails?.type === elementTypes.AUTHORIZATION_FIELD) {
              authFieldElement = question
              authFieldSection = sectionKey
            }
            if (
              questionDetails?.type === elementTypes.LINE_ITEMS ||
              questionDetails?.type === elementTypes.MATERIAL_PARTS
            ) {
              lastItemWithTotalAmountDue.sectionKey = sectionKey
              lastItemWithTotalAmountDue.questionKey = questionKey
              elementsWithTotalAmountDue.push({
                question,
                type: questionDetails.type,
                sectionKey,
                questionKey
              })
            }
          })
        })
      })
    })
    return {
      authFieldSection,
      authFieldElement,
      lastItemWithTotalAmountDue,
      elementsWithTotalAmountDue
    }
  }

  const previewModeSwitchHandler = () => {
    setIsPreviewMode(prevState => !prevState)
  }

  const checkNested = (obj, level, ...rest) => {
    if (obj === undefined) return false
    // eslint-disable-next-line no-prototype-builtins
    if (rest.length === 0 && obj.hasOwnProperty(level)) return true
    return checkNested(obj[level], ...rest)
  }
  const addTotalAmounts = repairData => {
    let total = 0
    const { elementsWithTotalAmountDue } = extraData
    if (configurations && repairData && elementsWithTotalAmountDue) {
      for (const element of elementsWithTotalAmountDue) {
        if (!element) continue
        const elementAnswer =
          repairData?.[element?.sectionKey]?.[element?.questionKey]
        if (
          element.type === elementTypes.MATERIAL_PARTS &&
          elementAnswer &&
          elementAnswer.length > 0
        ) {
          total += calculateMaterialsSubTotal(elementAnswer)
        }
        if (
          element.type === elementTypes.LINE_ITEMS &&
          elementAnswer &&
          elementAnswer.length > 0
        ) {
          total += calculateLineItemsSubTotal(elementAnswer)
        }
      }
    }

    return total
  }

  const changeDataValueHandler = (value, sectionId, questionId) => {
    const clonedData = JSON.parse(JSON.stringify(dataObject))
    const newObject = {
      ...clonedData,
      [sectionId]: { ...clonedData[sectionId], [questionId]: value }
    }
    totalAmountDueValueRef.current = addTotalAmounts(newObject)
    setDataObject(newObject)
  }

  const pressDownArrowHandler = idx => {
    setActiveDropDowns(prevItems =>
      prevItems.map((item, index) => {
        if (index === idx) {
          return !item
        } else {
          return item
        }
      })
    )
  }

  function getNested (obj, ...args) {
    return args.reduce((obj, level) => obj && obj[level], obj)
  }

  const footerComponent = () => {
    const footerElements = []
    const answersData = dataObject
    if (extraData?.elementsWithTotalAmountDue?.length > 0) {
      // iterate over elementsWithTotalAmountDue
      for (const footerElement of extraData?.elementsWithTotalAmountDue) {
        const objItem = footerElement?.question
        const sectionKey = footerElement?.sectionKey
        const questionKey = getFirstKeyFromObject(objItem)
        const questionData = objItem[questionKey]
        if (isPreviewMode) {
          const answer = getNested(answersData, sectionKey, questionKey)

          if (
            questionData?.title?.toLowerCase()?.includes('internal') ||
            !answer
          ) {
            continue
          }
        }

        footerElements.push(
          <EqServicesComponentV2
            key={questionKey}
            totalAmountDueValue={totalAmountDueValueRef.current}
            extraData={extraData}
            onPreviewModeSwitch={previewModeSwitchHandler}
            isPreviewMode={isPreviewMode}
            dataObject={dataObject}
            element={objItem}
            sectionId={sectionKey}
            onChangeDataValue={changeDataValueHandler}
          />
        )
      }
    }
    if (extraData?.authFieldElement && extraData?.authFieldSection) {
      const objItem = extraData?.authFieldElement
      const sectionKey = extraData?.authFieldSection
      const questionKey = getFirstKeyFromObject(objItem)
      footerElements.push(
        <EqServicesComponentV2
          key={questionKey}
          totalAmountDueValue={totalAmountDueValueRef.current}
          extraData={extraData}
          onPreviewModeSwitch={previewModeSwitchHandler}
          isPreviewMode={isPreviewMode}
          dataObject={dataObject}
          element={objItem}
          sectionId={sectionKey}
          onChangeDataValue={changeDataValueHandler}
        />
      )
    }
    return (
      <Fragment>
        {footerElements}
        <Box className={classes.footerBottomSpace} />
      </Fragment>
    )
  }

  const sectionItem = (question, sectionKey, sectionIndex) => {
    const objItem = question
    const sectionId = sectionKey

    const elementId = getFirstKeyFromObject(objItem)
    const questionData = objItem[elementId]
    if (
      questionData?.type === elementTypes.MATERIAL_PARTS ||
      questionData?.type === elementTypes.LINE_ITEMS ||
      questionData?.type === elementTypes.AUTHORIZATION_FIELD
    ) {
      return
    }
    if (isPreviewMode) {
      const answer = getNested(dataObject, sectionKey, elementId)
      if (
        questionData?.type !== elementTypes.AUTHORIZATION_FIELD &&
        (questionData?.title?.toLowerCase()?.includes('internal') || !answer)
      ) {
        return
      }
    }
    return (
      activeDropDowns[sectionIndex] && (
        <EqServicesComponentV2
          key={elementId}
          totalAmountDueValue={totalAmountDueValueRef.current}
          extraData={extraData}
          onPreviewModeSwitch={previewModeSwitchHandler}
          isPreviewMode={isPreviewMode}
          dataObject={dataObject}
          element={objItem}
          sectionId={sectionId}
          onChangeDataValue={changeDataValueHandler}
        />
      )
    )
  }

  const sectionHeader = section => {
    if (isPreviewMode) {
      const sectionKey = section.id
      const sectionIndex = section.index
      return (
        <Fragment>
          <ElementContainer applyShadow={true}>
            <Typography className={classes.sectionHeaderTitle}>
              {section.title}
            </Typography>
            <Divider component="li" className={classes.divider} />
            {section.data.map((item, index) => {
              return sectionItem(item, sectionKey, sectionIndex)
            })}
          </ElementContainer>
          <Box className={classes.headerBottomSpace} />
        </Fragment>
      )
    }
    return (
      <Box>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionHeaderTitle}>
            {section.title}
          </Typography>
          <IconButton
            disableRipple
            aria-label="delete"
            onClick={() => {
              pressDownArrowHandler(section.index)
            }}
          >
            <FontAwesomeIcon
              icon={activeDropDowns[section.index] ? faAngleDown : faAngleUp}
              className={classes.sectionHeaderArrow}
            />
          </IconButton>
        </Box>
        <Divider component="li" className={classes.dividerAlt} />
        {!activeDropDowns[section.index] && (
          <div
            className={classes.collapsedDropDown}
            onClick={() => {
              pressDownArrowHandler(section.index)
            }}
          >
            <Typography className={classes.collapsedText}>
              {t('mobile_preview.collapsed_message')}
            </Typography>
          </div>
        )}
      </Box>
    )
  }
  const navBarkey = `navBar-${formName}-${pendingObj.unresolvedTasks}-${pendingObj.percentage}`
  return (
    <Box className={classes.phoneContainer}>
      <Box className={classes.closeButtonContainer}>
        <Button
          variant="contained"
          startIcon={<FontAwesomeIcon icon={faXmark} />}
          className={classes.closeButton}
          onClick={onShowPreview}
        >
          {t('mobile_preview.cancel')}
        </Button>
      </Box>
      <Box>
        <div>
          <div className="device device-iphone-14-pro">
            <div className="device-frame">
              <Box className={classes.screenContainer}>
                <MobileNavigationBar
                  key={navBarkey}
                  formName={formName}
                  onPreviewModeSwitch={previewModeSwitchHandler}
                  pendingTasks={pendingObj.unresolvedTasks}
                  completePercentage={pendingObj.percentage}
                  configurations={configurations}
                  dataObject={dataObject}
                  pendingTasksCounter={pendingTasksCounter}
                  isPreviewMode={isPreviewMode}
                />
                <Box className={classes.elementsContainer}>
                  <SectionList
                    showsVerticalScrollIndicator={false}
                    windowSize={11}
                    ListFooterComponent={footerComponent()}
                    stickySectionHeadersEnabled={false}
                    sections={formattedSections}
                    keyExtractor={item => getFirstKeyFromObject(item)}
                    renderItem={item => {
                      if (isPreviewMode) return
                      const sectionKey = item.section.id
                      const objItem = item.item
                      const sectionIndex = item.section.index
                      return sectionItem(objItem, sectionKey, sectionIndex)
                    }}
                    renderSectionHeader={({ section }) =>
                      sectionHeader(section)
                    }
                  />
                </Box>
              </Box>
            </div>
            <div className="device-stripe"></div>
            <div className="device-header"></div>
            <div className="device-sensors"></div>
            <div className="device-btns"></div>
            <div className="device-power"></div>
            <div className="device-home"></div>
          </div>
        </div>
      </Box>
    </Box>
  )
}

export default FormPreview
